import { useSyncExternalStore } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ThemeMode = 'light' | 'dark';

const KEY = 'forever.admin.theme';
const listeners = new Set<() => void>();
let initialized = false;

function read(): ThemePreference {
  try {
    const stored = localStorage.getItem(KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

let preference = read();

function systemMode(): ThemeMode {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function currentMode(): ThemeMode {
  return preference === 'system' ? systemMode() : preference;
}

function apply(): void {
  const mode = currentMode();
  document.documentElement.setAttribute('data-theme', mode);
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', mode === 'dark' ? '#181818' : '#1b4e4d');
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot() {
  return `${preference}:${currentMode()}`;
}

function setPreference(next: ThemePreference) {
  preference = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // Tercih, depolama kullanılamadığında da bu oturumda paylaşılır.
  }
  apply();
}

/** Üst çubuk ve komut paleti aynı tercihi, aynı anda gösterir. */
export function useThemePreference() {
  const value = useSyncExternalStore(subscribe, snapshot, () => 'system:light');
  const [selected, mode] = value.split(':') as [ThemePreference, ThemeMode];
  return {
    preference: selected,
    mode,
    setPreference,
    toggle: () => setPreference(mode === 'dark' ? 'light' : 'dark'),
  };
}

/** Giriş ekranı dahil tüm uygulamada sistem temasını ve diğer sekmeleri izler. */
export function initTheme(): void {
  apply();
  if (initialized) return;
  initialized = true;
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (preference === 'system') apply();
  });
  window.addEventListener('storage', (event) => {
    if (event.key !== KEY && event.key !== null) return;
    preference = read();
    apply();
  });
}
